import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Modal, Row, Space, Statistic, Table, Typography, message } from "antd";
import * as XLSX from "xlsx";
import { isValid, isValidArray, isValidNumber } from "../../../utils/utilities";
import { roundNumber } from "../../../utils/math.utils";

const { Text } = Typography;

const normalizeHeader = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");

const pickHeaderRowIndex = (rows) => {
  for (let i = 0; i < Math.min(rows.length, 30); i++) {
    const row = rows[i] || [];
    if (row.some((c) => normalizeHeader(c) === "pnr" || normalizeHeader(c).includes("pnr"))) return i;
  }
  return 0;
};

const findColumnIndex = (headers, candidates) => {
  const norm = headers.map(normalizeHeader);
  for (const cand of candidates) {
    const idx = norm.findIndex((h) => h === cand || h.includes(cand));
    if (idx >= 0) return idx;
  }
  return -1;
};

const toNumber = (v) => {
  if (typeof v === "number") return v;
  if (v === null || v === undefined) return NaN;
  const s = String(v).replace(/,/g, "").trim();
  if (!s) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

export const RedbusExcelVerify = ({ bookingType, reportTickets }) => {
  const fileRef = useRef(null);
  const [excelRows, setExcelRows] = useState([]);
  const [diffRows, setDiffRows] = useState([]);
  const [missingRows, setMissingRows] = useState([]);
  const [meta, setMeta] = useState({ fileName: "", sheetName: "", totalExcelPnrs: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const reportTicketMap = useMemo(() => {
    const map = new Map();
    if (!isValidArray(reportTickets)) return map;
    reportTickets.forEach((t) => {
      if (!t) return;
      const pnr = String(t.pnr || "").trim();
      if (!pnr) return;
      const fare = isValidNumber(t.fare) ? roundNumber(t.fare) : undefined;
      map.set(pnr, { ...t, fare });
    });
    return map;
  }, [reportTickets]);

  const reset = () => {
    setExcelRows([]);
    setDiffRows([]);
    setMissingRows([]);
    setMeta({ fileName: "", sheetName: "", totalExcelPnrs: 0 });
    if (fileRef.current) fileRef.current.value = "";
  };

  const runCompare = (rows2d, fileName) => {
    const headerRowIndex = pickHeaderRowIndex(rows2d);
    const headers = (rows2d[headerRowIndex] || []).map((h) => (h === undefined ? "" : h));

    const pnrIdx = findColumnIndex(headers, ["pnr"]);
    const fareIdx = findColumnIndex(headers, ["ticket fare", "fare", "amount", "total fare", "ticket amount", "gross"]);

    if (pnrIdx < 0) {
      message.error("PNR column not found in Excel.");
      return;
    }

    const dataRows = rows2d
      .slice(headerRowIndex + 1)
      .filter((r) => isValidArray(r) && r.some((c) => isValid(c)));

    const cleaned = [];
    const diffs = [];
    const missing = [];

    dataRows.forEach((r, idx) => {
      const rawPnr = r[pnrIdx];
      const pnr = String(rawPnr || "").trim();
      if (!pnr) return;
      if (normalizeHeader(pnr) === "pnr") return; // skip repeated header rows inside sheet

      const excelFareRaw = fareIdx >= 0 ? r[fareIdx] : undefined;
      const excelFareNum = toNumber(excelFareRaw);
      const excelFare = Number.isFinite(excelFareNum) ? roundNumber(excelFareNum) : undefined;

      cleaned.push({ key: `${pnr}-${idx}`, pnr, excelFare });

      const report = reportTicketMap.get(pnr);
      if (!report) {
        missing.push({ key: `${pnr}-${idx}`, pnr, excelFare });
        return;
      }

      const reportFare = isValidNumber(report.fare) ? roundNumber(report.fare) : undefined;
      if (isValidNumber(excelFare) && isValidNumber(reportFare) && roundNumber(excelFare) !== roundNumber(reportFare)) {
        diffs.push({
          key: `${pnr}-${idx}`,
          pnr,
          excelFare,
          reportFare,
          diff: roundNumber(excelFare - reportFare),
          route: report.route,
          doj: report.start_date || report.journey_date,
          seat_numbers: report.seat_numbers,
          // status: report.ticket_status || report.status,
        });
      }
    });

    setExcelRows(cleaned);
    setDiffRows(diffs);
    setMissingRows(missing);
    setMeta({
      fileName,
      sheetName: "",
      totalExcelPnrs: cleaned.length,
    });
  };

  const onPickFile = async (file) => {
    try {
      if (!file) return;
      reset();

      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const firstSheetName = wb.SheetNames && wb.SheetNames[0];
      if (!firstSheetName) {
        message.error("Excel sheet not found.");
        return;
      }
      const sheet = wb.Sheets[firstSheetName];
      const rows2d = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
      if (!isValidArray(rows2d) || rows2d.length === 0) {
        message.error("Excel is empty.");
        return;
      }

      runCompare(rows2d, file.name);
      setMeta((m) => ({ ...m, sheetName: firstSheetName }));
      setIsModalOpen(true);
    } catch (e) {
      console.error(e);
      message.error("Failed to import Excel.");
    }
  };

  if (bookingType !== "red") return null;

  const diffColumns = [
    { title: "PNR", dataIndex: "pnr", key: "pnr" },
    { title: "Excel Fare", dataIndex: "excelFare", key: "excelFare" },
    { title: "Our Fare", dataIndex: "reportFare", key: "reportFare" },
    { title: "Diff", dataIndex: "diff", key: "diff" },
    { title: "Route", dataIndex: "route", key: "route" },
    { title: "DOJ", dataIndex: "doj", key: "doj" },
    // { title: "Status", dataIndex: "status", key: "status" },
  ];

  const missingColumns = [
    { title: "PNR (missing in our report)", dataIndex: "pnr", key: "pnr" },
    { title: "Excel Fare", dataIndex: "excelFare", key: "excelFare" },
  ];

  const totalExcel = meta.totalExcelPnrs || 0;
  const mismatchCount = diffRows.length;
  const missingCount = missingRows.length;
  const matchedCount = Math.max(totalExcel - missingCount - mismatchCount, 0);
  const okCount = Math.max(matchedCount, 0);

  return (
    <div style={{ display: "inline-block" }}>
      <Space size={8} wrap>
        <Button
          type="primary"
          onClick={() => {
            if (!isValidArray(reportTickets) || reportTickets.length === 0) {
              message.error("Run Get report first.");
              return;
            }
            if (fileRef.current) fileRef.current.click();
          }}
        >
          Verify Redbus (Import Excel)
        </Button>
        {meta.fileName && (
          <Button onClick={() => setIsModalOpen(true)}>{`View result (${mismatchCount} mismatch, ${missingCount} missing)`}</Button>
        )}
      </Space>

      <input
        ref={fileRef}
        type="file"
        accept=".xls,.xlsx"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          onPickFile(f);
        }}
      />

      <Modal
        title="Redbus Excel Verify"
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button
            key="import"
            onClick={() => {
              if (fileRef.current) fileRef.current.click();
            }}
          >
            Import another Excel
          </Button>,
          <Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>,
        ]}
        width={1100}
      >
        <div style={{ marginBottom: 12 }}>
          {meta.fileName ? (
            <Text>
              <Text strong>File:</Text> {meta.fileName}
            </Text>
          ) : (
            <Text type="secondary">Import `.xls/.xlsx` and compare PNR fare with our Get report.</Text>
          )}
        </div>

        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="Excel PNRs" value={totalExcel} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="Matched" value={matchedCount} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="Mismatch" value={mismatchCount} valueStyle={{ color: "#cf1322" }} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="Missing" value={missingCount} valueStyle={{ color: "#d48806" }} />
          </Col>
        </Row>

        {totalExcel > 0 && mismatchCount === 0 && missingCount === 0 && (
          <Alert type="success" showIcon message="All PNRs matched (no mismatch / no missing)." style={{ marginBottom: 12 }} />
        )}

        {mismatchCount > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              type="error"
              showIcon
              message={`Fare mismatch (${mismatchCount})`}
              description="These PNRs exist in our report but fare differs from the Excel OTA report."
              style={{ marginBottom: 8 }}
            />
            <Table
              columns={diffColumns}
              dataSource={diffRows}
              pagination={{ pageSize: 15, showSizeChanger: true }}
              size="small"
              scroll={{ x: 900, y: 280 }}
              sticky
            />
          </div>
        )}

        {missingCount > 0 && (
          <div style={{ marginBottom: 4 }}>
            <Alert
              type="warning"
              showIcon
              message={`Missing in our report (${missingCount})`}
              description="These PNRs are present in the Excel OTA report but not found in our report."
              style={{ marginBottom: 8 }}
            />
            <Table
              columns={missingColumns}
              dataSource={missingRows}
              pagination={{ pageSize: 15, showSizeChanger: true }}
              size="small"
              scroll={{ x: 500, y: 240 }}
              sticky
            />
          </div>
        )}

        {totalExcel > 0 && okCount > 0 && (
          <div style={{ marginTop: 10 }}>
            <Text type="secondary">
              Matched OK: <Text strong>{okCount}</Text>
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

